import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '../common';
import { api } from '../../utils';
import type { Match } from '../../types/match';
import { MATCH_STATUS } from '../../types/match';
import {
  getStatusColor,
  getStatusHandler,
  getStatusText,
} from '../../utils/match';
import { MdOutlineCalendarMonth } from 'react-icons/md';
import { DirectorChair, MapPin, Suitcase } from 'iconoir-react';
import { formatDate } from '../../utils';
import MatchExplanation from './MatchExplanation';
import IceBreakerSuggestions from './IceBreakerSuggestions';
import ReciprocityBadge from './ReciprocityBadge';
import MatchTierBadge from './MatchTierBadge';
import { Tooltip } from '@radix-ui/themes';
import ReportMenuButton from '../common/ReportMenuButton';
import { VALID_GOALS } from '../../services/eventService';

// Per-event goal slug -> human label (same vocabulary as the join goals picker).
const GOAL_LABEL: Record<string, string> = Object.fromEntries(
  VALID_GOALS.map((g) => [g.value, g.label]),
);

interface EventBadgeData {
  event_name: string;
  goal_complementary: boolean;
}

interface MatchCardProps {
  match: Match;
  variant?: 'horizontal' | 'vertical';
  className?: string;
  eventBadge?: EventBadgeData | null;
  onApprove?: () => Promise<void>;
  onReject?: (open: boolean) => Promise<void>;
}

const MatchCard = ({
  match,
  variant = 'vertical',
  className,
  eventBadge,
  onApprove,
  onReject,
}: MatchCardProps) => {
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const renderTime = useRef(Date.now());
  // Only a mutually-approved match (status 'approved') has a chat, so
  // interaction-based report reasons apply; pending / awaiting / passed do not.
  const isConnected = match.status_label?.toLowerCase() === MATCH_STATUS.APPROVED;

  // Per-event intent overlay: when the matched user set a goal FOR THIS event,
  // that is their authoritative objective here (mirrors what the matcher used),
  // shown in place of the global profile objective. Empty/null -> profile
  // objective, as before.
  const eventGoalLabels = (match.other_user.event_goals ?? [])
    .map((g) => GOAL_LABEL[g] ?? g)
    .filter(Boolean);
  const hasEventGoal = eventGoalLabels.length > 0;
  const objectiveValue = hasEventGoal
    ? eventGoalLabels.join(', ')
    : match.other_user.objective;
  const objectiveCaption = hasEventGoal
    ? `Looking for at ${match.event_name || 'this event'}`
    : 'Core Objectives';

  // Track match card viewed (fire-and-forget)
  useEffect(() => {
    api.post('/analytics/track', {
      event_type: 'match_card_viewed',
      event_category: 'matching',
      event_data: { match_id: match.id },
    }).catch(() => {});
  }, [match.id]);

  // Dwell time tracking — record how long user spends looking at this card
  const cardRef = useRef<HTMLDivElement>(null);
  const dwellStart = useRef<number | null>(null);
  const dwellTracked = useRef(false);

  const trackDwell = useCallback(() => {
    if (dwellStart.current && !dwellTracked.current) {
      const dwellMs = Date.now() - dwellStart.current;
      if (dwellMs >= 2000) { // Only track if viewed for 2+ seconds
        dwellTracked.current = true;
        api.post('/analytics/track', {
          event_type: 'match_view',
          event_category: 'matching',
          event_data: { match_id: match.id, dwell_time_ms: dwellMs },
        }).catch(() => {});
      }
    }
  }, [match.id]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          dwellStart.current = Date.now();
        } else {
          trackDwell();
          dwellStart.current = null;
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => { trackDwell(); observer.disconnect(); };
  }, [trackDwell]);

  // Apr-25: removed duplicate "Start Conversation" button. The
  // InitiateAIChatButton (rendered by getStatusHandler for APPROVED) is
  // ALREADY labeled "Start Conversation" and creates a real chat channel
  // via startDirectMessage(). The MatchCard-level Button below was
  // navigating with different query params (?matchId=&otherUserId= vs
  // ?channel=) and rendering for AWAITING_OTHER too — where the
  // status text says "waiting for them to approve" — so users saw a
  // CTA that contradicted the wait state. One CTA per state, sourced
  // from getStatusHandler.

  if (variant === 'horizontal') {
    return (
      <div
        ref={cardRef}
        className={`flex flex-col border border-purple-25 p-4 rounded-xl duration-200 ${className}`}
      >
        <div className='flex justify-between items-start gap-2 mb-6'>
          <div>
            <p className='!font-medium font-heading text-primary-500 !my-2 text-lg uppercase flex items-center gap-2 flex-wrap'>
              {match.other_user.name}
              {match.match_date && (Date.now() - new Date(match.match_date).getTime()) < 48 * 60 * 60 * 1000 && (
                <Tooltip content='Match created in the last 48 hours.'>
                  <span className='text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full normal-case cursor-help'>New</span>
                </Tooltip>
              )}
              <ReciprocityBadge reciprocal={match.reciprocal} size='sm' className='normal-case' />
              {/* Apr-25: surface tier on collapsed view */}
              {match.match_tier && (
                <MatchTierBadge tier={match.match_tier} size='xs' className='normal-case' />
              )}
              {match.event_name && (
                <span className='inline-flex items-center rounded-md bg-body-100 px-2 py-0.5 text-[11px] font-medium text-body-600'>
                  {match.event_name}
                </span>
              )}
            </p>
            {match.other_user.designation && match.other_user.designation !== 'Not specified' && match.other_user.designation !== 'Member' && (
              <p className='text-sm text-body-400 font-medium flex items-center gap-2 mb-0'>
                <Suitcase className='w-6 h-6' /> {match.other_user.designation}
              </p>
            )}
            {/* F6: location (physical city preferred, target-market region as
                fallback). Omit entirely when unknown — no "Location: Unknown". */}
            {(match.other_user.city || match.other_user.region) && (
              <p className='text-sm text-body-400 font-medium flex items-center gap-2 mb-0'>
                <MapPin className='w-6 h-6' /> {match.other_user.city || match.other_user.region}
              </p>
            )}
          </div>
          {/* Apr-25: status compatibility score stacked on the right
              so users see the score at a glance without expanding. */}
          <div className='flex flex-col items-end gap-1.5'>
            <ReportMenuButton userId={match.other_user.id} name={match.other_user.name} connected={isConnected} className='-mr-1' />
            <Badge
              className='text-capitalize'
              color={getStatusColor(match.status_label)}
              size='sm'
            >
              {getStatusText(match.status_label)}
            </Badge>
            {typeof match.compatibility_score === 'number' && (
              <Tooltip content='Overall compatibility score (0-100). Higher means more reciprocal value on paper.'>
                <span className='text-sm font-bold text-primary-500 cursor-help'>
                  {match.compatibility_score}%
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        {objectiveValue && (
          <>
            <p className='text-sm text-body-400 font-medium flex items-center gap-2 mb-1'>
              <DirectorChair className='w-6 h-6' /> {objectiveCaption}:
            </p>
            <p className='text-base leading-5 mb-6'>{objectiveValue}</p>
          </>
        )}
        {/* Jun-19 (a user V1 test): the dashboard (horizontal) cards previously
            showed only a name, so users couldn't tell who someone was before
            Pass/Connect. Add the same lazy "Why this match?" expander the full
            Matches page uses (fetches only on expand, so no dashboard perf hit). */}
        <MatchExplanation matchId={match.id} className='mb-4' />
        <div className='md:flex items-end gap-2'>
          <div className='w-full md:w-[calc(40%-0.5rem)]'>
            <p className='text-xs text-body-300 font-semibold flex items-center gap-1 mb-0'>
              <MdOutlineCalendarMonth size={13} />
              <span className='pt-[0.1rem]'>
                Date: {formatDate(match.match_date)}
              </span>
            </p>
          </div>
          <div className='w-full md:w-[calc(60%-0.5rem)] mt-4 md:mt-0 flex gap-2'>
            {getStatusHandler(
              match,
              variant,
              onApprove,
              onReject,
              openReviewModal,
              setOpenReviewModal
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={cardRef} className='flex flex-col border border-purple-25 sm:p-6 p-4 rounded-xl duration-200'>
      <div className='grow'>
        <div className='flex justify-between items-center mb-4'>
          <p className='text-xs text-body-300 font-semibold flex items-center gap-1 mb-0'>
            <MdOutlineCalendarMonth size={13} />
            <span className='pt-[0.1rem]'>
              Date: {formatDate(match.match_date)}
            </span>
          </p>
          {/* Apr-25: status compatibility score grouped on right so
              the score is visible WITHOUT expanding "Why this match?". */}
          <div className='flex items-center gap-2'>
            {typeof match.compatibility_score === 'number' && (
              <Tooltip content='Overall compatibility score (0-100). Higher means more reciprocal value on paper.'>
                <span className='text-sm font-bold text-primary-500 cursor-help'>
                  {match.compatibility_score}%
                </span>
              </Tooltip>
            )}
            <Badge
              className='text-capitalize'
              color={getStatusColor(match.status_label)}
              size='xs'
            >
              {getStatusText(match.status_label)}
            </Badge>
            <ReportMenuButton userId={match.other_user.id} name={match.other_user.name} connected={isConnected} />
          </div>
        </div>
        <p className='!font-medium font-heading text-primary-500 !my-2 text-lg uppercase flex items-center gap-2 flex-wrap'>
          {match.other_user.name}
          {match.match_date && (Date.now() - new Date(match.match_date).getTime()) < 48 * 60 * 60 * 1000 && (
            <Tooltip content='Match created in the last 48 hours.'>
              <span className='text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full normal-case cursor-help'>New</span>
            </Tooltip>
          )}
          <ReciprocityBadge reciprocal={match.reciprocal} size='sm' className='normal-case' />
          {/* Apr-25: tier badge surfaced on collapsed view */}
          {match.match_tier && (
            <MatchTierBadge tier={match.match_tier} size='xs' className='normal-case' />
          )}
          {match.event_name && (
            <span className='inline-flex items-center rounded-md bg-body-100 px-2 py-0.5 text-[11px] font-medium text-body-600'>
              {match.event_name}
            </span>
          )}
        </p>
        {eventBadge && (
          <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full mb-2 ${
            eventBadge.goal_complementary
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <span>Both at {eventBadge.event_name}</span>
            {eventBadge.goal_complementary && <span className="font-semibold">· Goals align</span>}
          </div>
        )}
        {match.other_user.designation && match.other_user.designation !== 'Not specified' && match.other_user.designation !== 'Member' && (
          <p className='text-xs text-body-400 font-medium flex items-center gap-2 mb-4'>
            <Suitcase className='w-6 h-6' /> {match.other_user.designation}
          </p>
        )}
        {/* F6: location — city preferred, region fallback, omit when unknown. */}
        {(match.other_user.city || match.other_user.region) && (
          <p className='text-xs text-body-400 font-medium flex items-center gap-2 mb-4'>
            <MapPin className='w-6 h-6' /> {match.other_user.city || match.other_user.region}
          </p>
        )}
        {objectiveValue && (
          <>
            <p className='text-sm text-body-400 font-medium flex items-center gap-2 mb-1'>
              <DirectorChair className='w-6 h-6' /> {objectiveCaption}:
            </p>
            <p className='text-sm leading-5 text-body-700 font-medium mb-4'>
              {objectiveValue}
            </p>
          </>
        )}
        {/* Phase 1.1: Match Explanation */}
        <MatchExplanation matchId={match.id} className="mb-4" />
        {/* Phase 2: Ice-Breaker Suggestions — only once the match is mutually
            approved. Before that there is no conversation to start, so showing
            them (and the chat-launch they trigger) contradicts the wait state. */}
        {match.status_label?.toLowerCase() === MATCH_STATUS.APPROVED && (
          <IceBreakerSuggestions
            matchId={match.id}
            otherUserId={match.other_user.id}
            className="mb-4"
          />
        )}
      </div>
      {getStatusHandler(
        match,
        variant,
        onApprove,
        onReject,
        openReviewModal,
        setOpenReviewModal
      )}
    </div>
  );
};

export default MatchCard;
